import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';

import authApi from '../api/AuthApi';
import OrganisationsApi from '../api/OrganisationsApi';
import TaxesApi from '../collums-components/api/TaxesApi';
import Intercom from '../collums-components/helpers/Intercom';
import Candu from '../collums-components/helpers/candu';

import { getQueryParams } from '../services/locationHelper';
import api, { enqueueLoginRedirect } from '../collums-components/api/config';
import { loadOrganisation } from '../actions/organisationActions';
import { TOKEN } from '../collums-constants/storageKeys';
import { getHasAdvancedPermissions, validateAndUpdateClinic } from '../collums-components/helpers/user';
import { validateJobPermissions, getCookieEnvironment } from '../collums-components/helpers/index';
import { isValidMongoIdString } from '../collums-constants/utils';

export default function BootstrapAuth({ onReady }) {
    const [cookies, setCookie] = useCookies([TOKEN]);
    const location = useLocation();
    const dispatch = useDispatch();

    const tokenCookie = cookies[TOKEN];
    const { token: queryToken, clinic } = getQueryParams(location);

    const lastBootstrapKeyRef = useRef(null);

    useEffect(() => {
        const currentBootstrapKey = JSON.stringify({ tokenCookie, queryToken, clinic });

        if (lastBootstrapKeyRef.current === currentBootstrapKey) {
            return;
        }

        lastBootstrapKeyRef.current = currentBootstrapKey;

        const stripTokenFromUrl = () => {
            try {
                const params = new URLSearchParams(window.location.search);
                if (params.has('token')) {
                    params.delete('token');
                    const newQuery = params.toString();
                    const baseUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
                    window.history.replaceState({}, '', `${baseUrl}${window.location.hash}`);
                }
            } catch {
                /* ignore */
            }
        };

        const bootstrap = async () => {
            // Migrate any leftover sessionStorage token to localStorage
            try {
                const sessionToken = sessionStorage.getItem(TOKEN);
                if (sessionToken) {
                    if (!localStorage.getItem(TOKEN)) {
                        localStorage.setItem(TOKEN, sessionToken);
                    }
                    sessionStorage.removeItem(TOKEN);
                }
                const sessionCurrency = sessionStorage.getItem('currency');
                if (sessionCurrency) {
                    localStorage.setItem('currency', sessionCurrency);
                    sessionStorage.removeItem('currency');
                }
            } catch {
                /* ignore */
            }

            const storedToken = localStorage.getItem(TOKEN);
            const token = queryToken || tokenCookie || storedToken;

            if (!token) {
                enqueueLoginRedirect();
                return;
            }

            localStorage.setItem(TOKEN, token);
            if (tokenCookie !== token) {
                lastBootstrapKeyRef.current = JSON.stringify({ tokenCookie: token, queryToken, clinic });
                setCookie('token', token, getCookieEnvironment());
            }
            stripTokenFromUrl();

            api.resetToken(token);

            try {
                const [me, organisation, taxes] = await Promise.all([
                    authApi.getMe(),
                    OrganisationsApi.listOrganisations(),
                    TaxesApi.listTaxes()
                ]);

                localStorage.setItem('currency', organisation.currency);

                dispatch(
                    loadOrganisation({
                        ...organisation,
                        taxes,
                        job: me.job,
                        role: me.role,
                        hasAdvancedPermissions: getHasAdvancedPermissions(me)
                    })
                );

                await validateJobPermissions(token, me, process.env.REACT_APP_NAME);

                if (clinic && isValidMongoIdString(clinic)) {
                    validateAndUpdateClinic(me, clinic);
                }

                try {
                    if (Intercom && typeof Intercom.init === 'function') {
                        Intercom.init();
                    }
                    if (Intercom && typeof Intercom.updateUser === 'function') {
                        Intercom.updateUser(me);
                    }
                } catch {
                    /* ignore */
                }

                try {
                    Candu.init(me);
                } catch {
                    /* ignore */
                }
            } catch (error) {
                if (error?.status === 401) {
                    localStorage.removeItem(TOKEN);

                    try {
                        if (Intercom && typeof Intercom.shutDown === 'function') {
                            Intercom.shutDown();
                        }
                        Candu.shutDown();
                    } catch {
                        /* ignore */
                    }

                    enqueueLoginRedirect();
                    return;
                }

                console.error(error);
                return;
            }

            onReady();
        };

        bootstrap().catch(err => {
            console.error(err);
        });
    }, [tokenCookie, queryToken, clinic, onReady, dispatch, setCookie]);

    return null;
}
